<template>
  <v-container
    fluid
    pa-0
    class="nx-searcher-page fill-height"
  >
    <nx-searcher
      :entity="entity"
      :filters="filters"
      :sort="sort"
      :text="text"
      @changeEntity="changeEntity"
      @setFilters="setFilters"
      @updateSort="updateSort"
    />
    <nx-select-entity-wizard :wizards="wizards" />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import BasePage from '@/pages/BasePage';
import { generateRoute } from '@/router';
import { omit, pick } from 'lodash';
import { toArray } from '@/utils';
import { multiValueFilters } from '@/components/filters/config';
import NxSelectEntityWizard from '@/modules/entity/wizard/WizardSelect';
import NxSearcher from '@/modules/searcher/Searcher';
import { decodeSortString, encodeSortString, injectDefaultParamsInNavigationGuard } from '../router';

function injectDefaultParams(to, from, next) {
  const simpleRoute = pick(to, ['name', 'params', 'query']);
  const { modified, location } = injectDefaultParamsInNavigationGuard(simpleRoute);
  if (modified) {
    return next(location);
  }
  return next();
}

export default {
  name: 'NxSearcherPage',
  components: {
    NxSelectEntityWizard,
    NxSearcher,
  },
  extends: BasePage,
  metaInfo() {
    return {
      title: this.$tc(`message.searcher.tab.${this.entity}`),
      // all titles will be injected into this template
      titleTemplate: '%s | BCube CMS',
    };
  },
  beforeRouteEnter: (to, from, next) => {
    injectDefaultParams(to, from, next);
  },
  beforeRouteUpdate: (to, from, next) => {
    injectDefaultParams(to, from, next);
  },
  computed: {
    ...mapGetters(['availableSearcherWizards', 'getWizard']),
    wizards() {
      return this.availableSearcherWizards(this.entity)
        .map(this.getWizard)
        .filter(wizard => !!wizard);
    },
    query() {
      return omit(this.$route.query, 'toast');
    },
    filters() {
      // Return a new filters object without the sort property
      return Object.keys(this.query)
        .filter(key => key !== 'sort')
        .reduce((filters, key) => {
          let value = this.query[key];
          if (this.currentEntityFilters[key]
            && multiValueFilters.includes(this.currentEntityFilters[key].component)) {
            value = toArray(value);
          }
          filters[key] = value;
          return filters;
        }, {});
    },
    sort() {
      // Query string sort
      if (this.query.sort) {
        return decodeSortString(this.query.sort);
      }
      return null;
    },
    // representa las agrupaciones del searcher, no entidades como tal
    // ej podemos tener contents, categories, etc
    // entidades serian article, image, video, section, tag, etc
    entity() {
      return this.$route.params.entity;
    },
    text() {
      return this.query.text;
    },
    currentEntityFilters() {
      return (this.$store.getters.searcher[this.entity].filters || []).reduce((acc, filter) => {
        acc[filter.filter] = filter;
        return acc;
      }, {});
    },
  },
  mounted() {
    // Load searcher config if is not present
    this.finishLoading();
  },
  methods: {
    updateSort(sort) {
      this.setQueryString({ filters: this.filters, sort });
    },
    setFilters(filters, silent = false) {
      this.setQueryString({ filters, silent });
    },
    setQueryString({ filters, sort = null, silent = false }) {
      filters.sort = encodeSortString(sort || this.sort);
      this.$router[silent ? 'replace' : 'push']({ query: filters });
    },
    changeEntity(entity) {
      this.$router.push(generateRoute({ name: 'searcher', params: { entity } }));
    },
  },
};
</script>
